import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  ClipboardList, ArrowLeft, Warehouse, Filter, Eye, EyeOff,
  Package, Layers, MapPin, List, AlertCircle, Info, Play, Save,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'

type Deposito = { id: number; nome: string; is_default?: boolean; ativo?: boolean }
type Categoria = { id: number; nome: string }
type Produto = { id: number; codigo: string; descricao: string; endereco_estoque: string | null; categoria_id: number | null; estoque_atual: number }

type FiltroTipo = 'nenhum' | 'categoria' | 'endereco' | 'produtos_especificos'

export default function InventarioNovoPage() {
  const navigate = useNavigate()

  // Step 1
  const [descricao, setDescricao] = useState('')
  const [depositoId, setDepositoId] = useState<number | ''>('')

  // Step 2
  const [tipo, setTipo] = useState<'completo' | 'parcial'>('completo')
  const [filtroTipo, setFiltroTipo] = useState<FiltroTipo>('nenhum')
  const [categoriaIds, setCategoriaIds] = useState<number[]>([])
  const [enderecos, setEnderecos] = useState<string>('')       // separados por virgula
  const [produtoIds, setProdutoIds] = useState<string>('')     // separados por virgula

  // Step 3
  const [modoCego, setModoCego] = useState(false)
  const [observacoes, setObservacoes] = useState('')

  // ---- data ----
  const { data: depositos = [] } = useQuery<Deposito[]>({
    queryKey: ['depositos-ativos'],
    queryFn: async () => (await api.get('/estoque/depositos')).data,
  })
  useEffect(() => {
    if (depositoId === '' && depositos.length) {
      const def = depositos.find(d => d.is_default) || depositos[0]
      setDepositoId(def.id)
    }
  }, [depositos, depositoId])

  const { data: categorias = [] } = useQuery<Categoria[]>({
    queryKey: ['categorias'],
    queryFn: async () => (await api.get('/cadastros/categorias')).data,
  })

  const { data: produtosAll = [] } = useQuery<Produto[]>({
    queryKey: ['produtos-all'],
    queryFn: async () => (await api.get('/cadastros/produtos', { params: { q: '', limit: 9999 } })).data,
  })

  // ---- preview ----
  const produtosNoEscopo = useMemo(() => {
    if (tipo === 'completo' || filtroTipo === 'nenhum') return produtosAll
    if (filtroTipo === 'categoria') {
      if (categoriaIds.length === 0) return []
      return produtosAll.filter(p => p.categoria_id && categoriaIds.includes(p.categoria_id))
    }
    if (filtroTipo === 'endereco') {
      const list = enderecos.split(',').map(s => s.trim()).filter(Boolean)
      if (list.length === 0) return []
      return produtosAll.filter(p => p.endereco_estoque && list.includes(p.endereco_estoque))
    }
    if (filtroTipo === 'produtos_especificos') {
      const ids = produtoIds.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n))
      if (ids.length === 0) return []
      return produtosAll.filter(p => ids.includes(p.id))
    }
    return []
  }, [produtosAll, tipo, filtroTipo, categoriaIds, enderecos, produtoIds])

  // ---- build payload ----
  function buildPayload(tipoEfetivo: 'completo' | 'parcial') {
    const payload: any = {
      descricao: descricao || null,
      deposito_id: depositoId,
      tipo: tipoEfetivo,
      modo_cego: modoCego,
      observacoes: observacoes || null,
    }
    if (tipoEfetivo === 'completo') {
      payload.filtro_tipo = 'nenhum'
      payload.filtro_valor = null
    } else {
      payload.filtro_tipo = filtroTipo
      if (filtroTipo === 'categoria') {
        payload.filtro_valor = { categoria_ids: categoriaIds }
      } else if (filtroTipo === 'endereco') {
        payload.filtro_valor = {
          enderecos: enderecos.split(',').map(s => s.trim()).filter(Boolean),
        }
      } else if (filtroTipo === 'produtos_especificos') {
        payload.filtro_valor = {
          produto_ids: produtoIds.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n)),
        }
      } else {
        payload.filtro_valor = null
      }
    }
    return payload
  }

  // ---- mutations ----
  const criar = useMutation({
    mutationFn: async (iniciarAgora: boolean) => {
      const tipoEf = tipo
      const r = await api.post('/inventarios', buildPayload(tipoEf))
      if (iniciarAgora) {
        await api.post(`/inventarios/${r.data.id}/iniciar`)
      }
      return { ...r.data, iniciado: iniciarAgora }
    },
    onSuccess: (r) => {
      if (r.iniciado) {
        toast.success(`${r.codigo} iniciado — produtos bloqueados pra venda`)
        navigate(`/estoque/inventarios/${r.id}`)
      } else {
        toast.success(`${r.codigo} criado como rascunho`)
        navigate('/estoque/inventarios')
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao criar inventário'),
  })

  const podeCriar = depositoId !== '' && (
    tipo === 'completo' ||
    filtroTipo === 'nenhum' ||
    produtosNoEscopo.length > 0
  )

  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList size={24} /> Novo inventário
          </h1>
          <p className="text-ink-500 text-sm">Configure o escopo e inicie a contagem.</p>
        </div>
        <button onClick={() => navigate('/estoque/inventarios')} className="btn btn-ghost flex items-center gap-1">
          <ArrowLeft size={14} /> Voltar
        </button>
      </div>

      {/* Step 1 — Identificação */}
      <div className="card card-body space-y-3">
        <h3 className="font-semibold text-sm uppercase tracking-wide text-ink-700">1. Identificação</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <label className="label">Descrição</label>
            <input
              className="input"
              value={descricao}
              onChange={e => setDescricao(e.target.value)}
              placeholder="Ex: Inventário anual 2026, Contagem setor bebidas, etc."
            />
          </div>
          <div>
            <label className="label flex items-center gap-1"><Warehouse size={12} /> Depósito *</label>
            <select
              className="input"
              value={depositoId}
              onChange={e => setDepositoId(Number(e.target.value))}
            >
              {depositos.map(d => (
                <option key={d.id} value={d.id}>{d.nome}{d.is_default ? ' ★' : ''}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Step 2 — Escopo */}
      <div className="card card-body space-y-3">
        <h3 className="font-semibold text-sm uppercase tracking-wide text-ink-700">2. Escopo</h3>

        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => { setTipo('completo'); setFiltroTipo('nenhum') }}
            className={`p-4 rounded-lg border-2 text-left transition ${
              tipo === 'completo'
                ? 'border-brand-500 bg-brand-50'
                : 'border-ink-200 hover:border-ink-300'
            }`}
          >
            <div className="flex items-center gap-2 font-semibold mb-1">
              <Layers size={16} /> Completo
            </div>
            <div className="text-xs text-ink-500">Todos os produtos ativos da empresa</div>
          </button>
          <button
            type="button"
            onClick={() => setTipo('parcial')}
            className={`p-4 rounded-lg border-2 text-left transition ${
              tipo === 'parcial'
                ? 'border-brand-500 bg-brand-50'
                : 'border-ink-200 hover:border-ink-300'
            }`}
          >
            <div className="flex items-center gap-2 font-semibold mb-1">
              <Filter size={16} /> Parcial
            </div>
            <div className="text-xs text-ink-500">Filtrar por categoria, endereço ou produtos</div>
          </button>
        </div>

        {tipo === 'parcial' && (
          <div className="space-y-3 bg-ink-50 p-3 rounded border border-ink-200">
            <div>
              <label className="label">Filtrar por</label>
              <select
                className="input"
                value={filtroTipo}
                onChange={e => setFiltroTipo(e.target.value as FiltroTipo)}
              >
                <option value="nenhum">— todos os produtos —</option>
                <option value="categoria">Categoria</option>
                <option value="endereco">Endereço de estoque</option>
                <option value="produtos_especificos">Produtos específicos</option>
              </select>
            </div>

            {filtroTipo === 'categoria' && (
              <div>
                <label className="label">Categorias</label>
                <div className="flex flex-wrap gap-2">
                  {categorias.map(c => {
                    const checked = categoriaIds.includes(c.id)
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() =>
                          setCategoriaIds(prev =>
                            checked ? prev.filter(x => x !== c.id) : [...prev, c.id]
                          )
                        }
                        className={`px-3 py-1 rounded-full text-xs border ${
                          checked
                            ? 'bg-brand-600 text-white border-brand-600'
                            : 'bg-white text-ink-700 border-ink-300 hover:border-ink-500'
                        }`}
                      >
                        {c.nome}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {filtroTipo === 'endereco' && (
              <div>
                <label className="label flex items-center gap-1"><MapPin size={12} /> Endereços (separados por vírgula)</label>
                <input
                  className="input"
                  value={enderecos}
                  onChange={e => setEnderecos(e.target.value)}
                  placeholder="Ex: A01, A02, B03"
                />
              </div>
            )}

            {filtroTipo === 'produtos_especificos' && (
              <div>
                <label className="label flex items-center gap-1"><List size={12} /> IDs dos produtos (separados por vírgula)</label>
                <input
                  className="input"
                  value={produtoIds}
                  onChange={e => setProdutoIds(e.target.value)}
                  placeholder="Ex: 9, 10, 23"
                />
                <p className="text-xs text-ink-500 mt-1">
                  Use a tela de produtos pra copiar os IDs desejados.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Preview */}
        <div className={`p-3 rounded border flex items-start gap-2 ${
          produtosNoEscopo.length === 0
            ? 'bg-amber-50 border-amber-300 text-amber-800'
            : 'bg-emerald-50 border-emerald-300 text-emerald-800'
        }`}>
          {produtosNoEscopo.length === 0 ? <AlertCircle size={18} /> : <Package size={18} />}
          <div>
            <div className="font-semibold text-sm">
              {produtosNoEscopo.length === 0
                ? 'Nenhum produto no escopo'
                : `${produtosNoEscopo.length} produto${produtosNoEscopo.length !== 1 ? 's' : ''} no escopo`}
            </div>
            {produtosNoEscopo.length > 0 && produtosNoEscopo.length <= 8 && (
              <div className="text-xs mt-1">
                {produtosNoEscopo.map(p => p.codigo).join(', ')}
              </div>
            )}
            {produtosNoEscopo.length > 8 && (
              <div className="text-xs mt-1">
                Primeiros: {produtosNoEscopo.slice(0, 6).map(p => p.codigo).join(', ')}…
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Step 3 — Configuração */}
      <div className="card card-body space-y-3">
        <h3 className="font-semibold text-sm uppercase tracking-wide text-ink-700">3. Configuração</h3>

        <label className="flex items-start gap-3 cursor-pointer p-3 border border-ink-200 rounded hover:bg-ink-50">
          <input
            type="checkbox"
            checked={modoCego}
            onChange={e => setModoCego(e.target.checked)}
            className="mt-1"
          />
          <div>
            <div className="font-semibold text-sm flex items-center gap-2">
              {modoCego ? <EyeOff size={14} /> : <Eye size={14} />}
              Modo cego
            </div>
            <div className="text-xs text-ink-500 mt-1">
              O contador não vê a quantidade esperada. Todo item é marcado como "divergente" e passa por conferência antes da aprovação.
              Recomendado para inventários que exigem auditoria rigorosa.
            </div>
          </div>
        </label>

        <div>
          <label className="label">Observações</label>
          <textarea
            className="input"
            rows={2}
            value={observacoes}
            onChange={e => setObservacoes(e.target.value)}
            placeholder="Notas internas sobre este inventário"
          />
        </div>
      </div>

      {/* Ações */}
      <div className="card card-body bg-ink-50 border-ink-200">
        <div className="flex items-start gap-2 text-sm text-ink-700 mb-3">
          <Info size={16} className="text-brand-600 mt-0.5" />
          <div>
            <strong>Criar rascunho</strong> deixa o inventário pronto, mas não bloqueia venda nem faz snapshot.
            <br />
            <strong>Iniciar agora</strong> congela o estoque atual como "esperado" e bloqueia venda dos produtos do escopo até a aprovação.
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button
            disabled={!podeCriar || criar.isPending}
            onClick={() => criar.mutate(false)}
            className="btn btn-ghost flex items-center gap-2"
          >
            <Save size={14} /> Criar rascunho
          </button>
          <button
            disabled={!podeCriar || criar.isPending}
            onClick={() => criar.mutate(true)}
            className="btn btn-primary flex items-center gap-2"
          >
            <Play size={14} /> Criar e iniciar
          </button>
        </div>
      </div>
    </div>
  )
}
